/**
 * Cell Allocation — pure helpers shared by the Allocation tab, its tests and
 * the service. No I/O here.
 *
 * Canon (glossary, "Cell Allocation (portfolio)"): the portfolio sets the
 * SHARE each comparison cell (item category × altitude) receives; every track
 * ranks freely inside its own cell. Allocation replaces cross-cell ranking.
 *
 * The database is the authority on these rules (migrations 816 and 972,
 * set_cell_allocation refuses what validateBands refuses). The copy here lets
 * the screen say what is wrong before a round trip; it never replaces the
 * server's check.
 */

import {
  ALTITUDE_NAMES_BY_CATEGORY,
  ALTITUDE_LEVELS_ORDERED,
  type AltitudeLevel,
} from '../types/prioritization/itemLifecycle';
import { formatAltitude } from './altitudeDisplay';

export interface CellRef {
  itemCategory: string;
  altitudeLevel: number;
}

/** A band as the screen edits it. `id` is absent until saved. */
export interface CellAllocationBandDraft {
  id?: string;
  name: string;
  sharePercent: number;
  cells: CellRef[];
}

/** The six canonical categories, in the order the grid shows them. */
export const CANONICAL_CATEGORIES: readonly string[] = Object.keys(ALTITUDE_NAMES_BY_CATEGORY);

export const ALTITUDES_HIGH_TO_LOW: readonly AltitudeLevel[] = ALTITUDE_LEVELS_ORDERED;

/**
 * Share verdict tolerance, in percentage points — the same as the database's
 * (migration 972, owner ruling R-2: keep two points).
 */
export const VERDICT_TOLERANCE_POINTS = 2;

export function cellKey(cell: CellRef): string {
  return `${cell.itemCategory.toLowerCase()}:${cell.altitudeLevel}`;
}

export function sameCell(a: CellRef, b: CellRef): boolean {
  return cellKey(a) === cellKey(b);
}

/** "Solution · Altitude 3 · Epic" — the cell's name the way the product says it: the altitude's number, then its name as a tag. */
export function cellLabel(cell: CellRef): string {
  const key = cell.itemCategory.toLowerCase();
  const cat = key.charAt(0).toUpperCase() + key.slice(1);
  return `${cat} · ${formatAltitude(cell.altitudeLevel, { category: key }) ?? `Altitude ${cell.altitudeLevel}`}`;
}

export function shareTotal(bands: readonly CellAllocationBandDraft[]): number {
  return Math.round(bands.reduce((sum, b) => sum + (Number.isFinite(b.sharePercent) ? b.sharePercent : 0), 0) * 100) / 100;
}

/**
 * The rules set_cell_allocation enforces, as one message or null.
 * Order matches the server so the first problem named is the same one.
 */
export function validateBands(bands: readonly CellAllocationBandDraft[]): string | null {
  const names = new Set<string>();
  const cells = new Set<string>();
  for (const band of bands) {
    const name = band.name.trim();
    if (!name) return 'Every band needs a name.';
    const lower = name.toLowerCase();
    if (names.has(lower)) return `Two bands are named "${name}".`;
    names.add(lower);
    if (!Number.isFinite(band.sharePercent) || band.sharePercent < 0 || band.sharePercent > 100) {
      return `Band "${name}": the share must be a number from 0 to 100.`;
    }
    if (band.cells.length === 0) return `Band "${name}" covers no cells.`;
    for (const cell of band.cells) {
      if (!cell.itemCategory.trim() || !Number.isInteger(cell.altitudeLevel) || cell.altitudeLevel < 1 || cell.altitudeLevel > 5) {
        return `Band "${name}": a cell is an item category and an altitude from 1 to 5.`;
      }
      const key = cellKey(cell);
      if (cells.has(key)) {
        return `The cell ${cellLabel(cell)} is in two bands. A cell draws from one envelope.`;
      }
      cells.add(key);
    }
  }
  // Owner ruling R-1 (migration 972): the shares do not have to add up to 100.
  // A total over 100 is saved too; shareTotalNote says what it means.
  return null;
}

/**
 * What the shares' total means, in plain words, or null when bands are empty.
 * Over 100 is a warning (owner ruling R-1): the bands promise more than all of
 * the committed work, so at least one of them will read under its share.
 */
export function shareTotalNote(bands: readonly CellAllocationBandDraft[]): { tone: 'green' | 'neutral' | 'coral'; text: string } | null {
  if (bands.length === 0) return null;
  const total = shareTotal(bands);
  if (total > 100) {
    return {
      tone: 'coral',
      text: `The shares add up to ${total}%, more than all of the committed work. At least one band will read under its share.`,
    };
  }
  if (total < 100) {
    const left = Math.round((100 - total) * 100) / 100;
    return { tone: 'neutral', text: `The shares add up to ${total}%. The other ${left}% is planned for no band.` };
  }
  return { tone: 'green', text: 'The shares add up to 100%.' };
}

/** The grid's cells that no band covers, high altitude first, category order as drawn. */
export function uncoveredCells(bands: readonly CellAllocationBandDraft[], categories: readonly string[]): CellRef[] {
  const out: CellRef[] = [];
  for (const alt of ALTITUDES_HIGH_TO_LOW) {
    for (const cat of categories) {
      const cell = { itemCategory: cat.toLowerCase(), altitudeLevel: alt };
      if (bandIndexForCell(bands, cell) < 0) out.push(cell);
    }
  }
  return out;
}

/** One band as it stands now and as it would stand if a proposal were accepted. */
export interface BandIfAccepted {
  name: string;
  sharePercent: number;
  nowShare: number | null;
  afterShare: number | null;
  afterVerdict: ShareVerdict;
}

/**
 * The bands an acceptance would leave over or under their share, among those
 * whose committed share it moves. A band the acceptance does not touch is not
 * news at the moment of accepting, so it is left out.
 */
export function bandsOffIfAccepted(
  now: readonly { id: string; committedShare: number | null }[],
  after: readonly { id: string; name: string; sharePercent: number; committedShare: number | null; verdict: ShareVerdict }[],
): BandIfAccepted[] {
  const before = new Map(now.map((b) => [b.id, b.committedShare]));
  return after
    .filter((b) => (b.verdict === 'over' || b.verdict === 'under') && before.get(b.id) !== b.committedShare)
    .map((b) => ({
      name: b.name,
      sharePercent: b.sharePercent,
      nowShare: before.get(b.id) ?? null,
      afterShare: b.committedShare,
      afterVerdict: b.verdict,
    }));
}

/** "Strategic bets goes from 50% to 71% of the committed work, against a 40% share: over by 31 points." */
export function bandIfAcceptedSentence(b: BandIfAccepted): string {
  const pct = (n: number | null) => (n == null ? 'nothing' : `${Number.isInteger(n) ? n : n.toFixed(1)}%`);
  const gap = b.afterShare == null ? 0 : Math.round(Math.abs(b.afterShare - b.sharePercent) * 10) / 10;
  const side = b.afterVerdict === 'over' ? 'over' : 'under';
  return `${b.name} goes from ${pct(b.nowShare)} to ${pct(b.afterShare)} of the committed work, against a ${pct(b.sharePercent)} share: ${side} by ${gap} point${gap === 1 ? '' : 's'}.`;
}

/** Which band (index) a cell belongs to, or -1. */
export function bandIndexForCell(bands: readonly CellAllocationBandDraft[], cell: CellRef): number {
  return bands.findIndex((b) => b.cells.some((c) => sameCell(c, cell)));
}

/** Toggle a cell in band `index`; a cell held by another band moves to this one. */
export function toggleCellInBand(
  bands: readonly CellAllocationBandDraft[],
  index: number,
  cell: CellRef,
): CellAllocationBandDraft[] {
  return bands.map((band, i) => {
    const has = band.cells.some((c) => sameCell(c, cell));
    if (i === index) {
      return has
        ? { ...band, cells: band.cells.filter((c) => !sameCell(c, cell)) }
        : { ...band, cells: [...band.cells, { itemCategory: cell.itemCategory.toLowerCase(), altitudeLevel: cell.altitudeLevel }] };
    }
    return has ? { ...band, cells: band.cells.filter((c) => !sameCell(c, cell)) } : band;
  });
}

export type ShareVerdict = 'over' | 'under' | 'on' | 'empty';

/** The same comparison the database makes (migration 972), for display of drafts. */
export function shareVerdict(committedShare: number | null, sharePercent: number): ShareVerdict {
  if (committedShare == null) return 'empty';
  if (committedShare > sharePercent + VERDICT_TOLERANCE_POINTS) return 'over';
  if (committedShare < sharePercent - VERDICT_TOLERANCE_POINTS) return 'under';
  return 'on';
}

/** Plain words for a verdict, with the gap in points when there is one. */
export function verdictLabel(v: ShareVerdict, gapPoints?: number | null): string {
  const pts = gapPoints == null ? null : Math.round(Math.abs(gapPoints) * 10) / 10;
  const by = pts == null || pts === 0 ? '' : ` by ${pts} point${pts === 1 ? '' : 's'}`;
  switch (v) {
    case 'over': return `Over its share${by}`;
    case 'under': return `Under its share${by}`;
    case 'on': return pts ? `On its share (${gapPoints! > 0 ? '+' : '−'}${pts})` : 'On its share';
    default: return 'Nothing committed yet';
  }
}

/** Categories to draw: the six canonical ones, then any the cycle's items add. */
export function gridCategories(extra: readonly string[]): string[] {
  const out = [...CANONICAL_CATEGORIES];
  for (const c of extra) {
    const k = c.toLowerCase();
    if (!out.includes(k)) out.push(k);
  }
  return out;
}
